import { Component } from '@theme/component';

/**
 * "See more" control for the product-list section, goodeggs-style: instead of navigating to a
 * new page, it fetches the next page of the section's collection via the Section Rendering API
 * and appends the new cards to the existing grid in place.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} seeMoreButton
 *
 * @extends {Component<Refs>}
 */
export class ProductListLoadMore extends Component {
  requiredRefs = ['seeMoreButton'];

  /**
   * Fetches the next page of results and appends the new cards to the grid.
   * @param {Event} event
   */
  loadMore = async (event) => {
    event.preventDefault();

    const nextUrl = this.dataset.nextUrl;
    const sectionId = this.dataset.sectionId;
    if (!nextUrl || !sectionId || this.refs.seeMoreButton.disabled) return;

    const gridSelector = this.dataset.gridSelector || '.resource-list';
    const currentGrid = this.closest('[data-testid="product-list"]')?.querySelector(gridSelector);
    if (!currentGrid) return;

    this.refs.seeMoreButton.disabled = true;
    this.toggleAttribute('loading', true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Failed to load more products: ${response.status}`);

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const newGrid = html.querySelector(gridSelector);
      if (newGrid) {
        const fragment = document.createDocumentFragment();
        while (newGrid.firstElementChild) {
          fragment.appendChild(newGrid.firstElementChild);
        }
        currentGrid.appendChild(fragment);
      }

      const newLoadMore = html.querySelector('product-list-load-more');
      const newNextUrl = newLoadMore instanceof HTMLElement ? newLoadMore.dataset.nextUrl : undefined;

      if (newNextUrl) {
        this.dataset.nextUrl = newNextUrl;
        this.refs.seeMoreButton.disabled = false;
      } else {
        // No more pages — remove the control entirely.
        this.remove();
      }
    } catch (error) {
      console.error('[product-list-load-more]', error);
      this.refs.seeMoreButton.disabled = false;
    } finally {
      this.toggleAttribute('loading', false);
    }
  };
}

if (!customElements.get('product-list-load-more')) {
  customElements.define('product-list-load-more', ProductListLoadMore);
}
